import React, { useState, useRef, useMemo } from 'react'
import { connect } from 'react-redux'
import { injectIntl, WrappedComponentProps } from 'react-intl'
import { ListItem, Button } from '@material-ui/core'
import { List, Map } from 'immutable'
import isEqual from 'lodash/isEqual'
import DateFnsUtils from '@date-io/date-fns'
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers'
import ProfileHelper from 'helpers/profile'
import {
  FILTER_TYPE_ASSIGNED_USER,
  FILTER_TYPE_ASSIGNED_USER_GROUP,
  FILTER_TYPE_ASSIGNEE,
  FILTER_TYPE_CREATED_BY,
  FILTER_TYPE_MEDIA,
  FILTER_TYPE_DUE_DATE,
  FILTER_TYPE_RECURRENCE,
  FILTER_TYPE_STATUS,
  MANAGEMENT_UI_MEDIA_FILTER,
  MANAGEMENT_UI_OTHER_FILTERS,
  MANAGEMENT_UI_RECURRENCE_FILTER,
  MANAGEMENT_UI_STATUS_FILTER,
  MANAGEMENT_UI_SCHEDULING_FILTERS,
} from 'constants/managementUI'
import { setSelectedOtherFilters } from 'reducers/managementUI/reducer'
import { dateToLocalISO8601DateString } from 'helpers/dates'
import { saveManagementUIFilters } from 'reducers/profile/reducer'
import { Flex } from 'components/common/styles'
import { User, UserGroup, Site, Licensee } from 'types/common'
import { ReactComponent as Vector } from 'assets/img/select-vector.svg'
import { ReactComponent as DatesIcon } from 'assets/img/calendar-icon.svg'
import SelectInput from 'components/common/Inputs/SelectInput'
import Menu, { MenuTitle } from 'components/common/Menu'
import messages from 'routes/App/Apps/ManagementUI/translate'
import OtherFilters from 'routes/App/Apps/ManagementUI/common/OtherFiltersDropdown/OtherFiltersDropdown'
import RemoveFilterButton from 'routes/App/Apps/ManagementUI/common/RemoveFilterButton/RemoveFilterButton'
import {
  SelectedOtherFilter,
  JobsManagementFilters,
} from 'types/managementUI.types'
import withPromise from 'helpers/withPromise'
import UsersListComponent from '../UsersList/UsersList'
import UsersListDropdown from '../UsersListDropdown/UsersListDropdown'
import UsersSelectList from '../UsersSelectList/UsersSelectList'
import {
  AddFilterButton,
  OtherFilterListItem,
  UsersFilterButton,
  DatePickerContainer,
  FilterSelectContainer,
} from './SelectedOtherFiltersDropdown.styles'
import { SelectedOtherFiltersDropdownProps } from './SelectedOtherFiltersDropdown.types'

// eslint-disable-next-line max-statements
export const SelectedOtherFiltersDropdown = ({
  intl: { formatMessage },
  selectedOtherFilters,
  setSelectedOtherFilters,
  filtersList,
  otherFiltersMenuAnchor,
  setOtherFiltersMenuAnchor,
  assigneesSearchQuery,
  setAssigneesSearchQuery,
  createdBySearchQuery,
  setCreatedBySearchQuery,
  tmpCreatedBy,
  setTmpCreatedBy,
  tmpRecurrence,
  setTmpRecurrence,
  tmpStatus,
  setTmpStatus,
  tmpUsers,
  setTmpUsers,
  tmpUserGroups,
  setTmpUserGroups,
  filteredUsersListFilter,
  filteredUserGroupsListFilter,
  filteredCreatedByListFilter,
  jobsManagementFilters,
  applyFilter,
  setIsUpdating,
  saveManagementUIFilters,
  licensee: { usaDateFormat },
  activeTab,
  isSchedulingTab,
  clearPagination,
}: SelectedOtherFiltersDropdownProps & WrappedComponentProps) => {
  const selectedOtherFiltersData = selectedOtherFilters.toJS()
  const filtersListData = filtersList.toJS()
  const {
    [FILTER_TYPE_ASSIGNED_USER]: usersListFilter,
    [FILTER_TYPE_ASSIGNED_USER_GROUP]: userGroupsListFilter,
  } = filtersListData
  const {
    [FILTER_TYPE_ASSIGNED_USER]: appliedUsers,
    [FILTER_TYPE_ASSIGNED_USER_GROUP]: appliedUserGroups,
    [FILTER_TYPE_CREATED_BY]: appliedCreatedBy,
    [FILTER_TYPE_MEDIA]: appliedMedia,
    [FILTER_TYPE_DUE_DATE]: appliedDueDate,
    [FILTER_TYPE_RECURRENCE]: appliedRecurrence,
    [FILTER_TYPE_STATUS]: appliedStatus,
  } = jobsManagementFilters

  const selectedUsers = useMemo(
    () =>
      usersListFilter.filter(
        (user: User) =>
          appliedUsers.length &&
          appliedUsers.find((id: number) => user.id === id),
      ),
    [JSON.stringify(usersListFilter), JSON.stringify(appliedUsers)],
  )

  const selectedUserGroups = useMemo(
    () =>
      userGroupsListFilter.filter((userGroup: UserGroup) =>
        appliedUserGroups.find((id: number) => userGroup.id === id),
      ),
    [JSON.stringify(userGroupsListFilter), JSON.stringify(appliedUserGroups)],
  )

  const selectedCreatedBy = useMemo(
    () =>
      usersListFilter.filter(
        (user: User) =>
          appliedCreatedBy.length &&
          appliedCreatedBy.find((id: number) => user.id === id),
      ),
    [JSON.stringify(usersListFilter), JSON.stringify(appliedCreatedBy)],
  )

  const dateFormat = usaDateFormat ? 'MM-dd-yyyy' : 'dd-MM-yyyy'

  const filtersOnSelectedTab = useMemo(
    () =>
      isSchedulingTab
        ? MANAGEMENT_UI_SCHEDULING_FILTERS
        : MANAGEMENT_UI_OTHER_FILTERS,
    [isSchedulingTab],
  )
  const selectedFiltersForTab = useMemo(
    () =>
      selectedOtherFiltersData.filter(
        ({ value }: { value: string }) =>
          !isSchedulingTab || MANAGEMENT_UI_SCHEDULING_FILTERS.includes(value),
      ),
    [JSON.stringify(selectedOtherFiltersData), isSchedulingTab],
  )
  const allOtherFiltersSelected = useMemo(
    () => selectedFiltersForTab.length === filtersOnSelectedTab.length,
    [selectedFiltersForTab.length, filtersOnSelectedTab.length],
  )
  const locale = ProfileHelper.getCurrentLocale()

  const [prevUsers, setPrevUsers] = useState<User[]>([])
  const [prevCreatedBy, setPrevCreatedBy] = useState<User[]>([])
  const [prevUserGroups, setPrevUserGroups] = useState<UserGroup[]>([])
  const [
    otherFiltersSubmenuAnchor,
    setOtherFiltersSubmenuAnchor,
  ] = useState<HTMLElement | null>(null)
  const [
    assigneeMenuAnchor,
    setAssigneeMenuAnchor,
  ] = useState<HTMLElement | null>(null)
  const [
    createdByMenuAnchor,
    setCreatedByMenuAnchor,
  ] = useState<HTMLElement | null>(null)

  const assigneesSearchFieldRef = useRef<HTMLElement>(null)
  const createdBySearchFieldRef = useRef<HTMLElement>(null)

  const getUpdatedOtherFiltersArr = (filterType: string) =>
    selectedFiltersForTab.filter(
      ({ value }: SelectedOtherFilter) => value !== filterType,
    )

  const applyAssignees = (dataUsers: number[], dataUserGroups: number[]) => {
    const saveFiltersPayload = {
      jobs_management_filters: {
        [FILTER_TYPE_ASSIGNED_USER]: dataUsers,
        [FILTER_TYPE_ASSIGNED_USER_GROUP]: dataUserGroups,
      },
    }

    clearPagination()
    setIsUpdating(true)
    withPromise(saveManagementUIFilters, saveFiltersPayload).then(() => {
      setIsUpdating(false)
    })
  }

  const applyOtherCreatedBy = (data: number[]) => {
    const saveFiltersPayload = {
      jobs_management_filters: {
        [FILTER_TYPE_CREATED_BY]: data || [],
      },
    }
    clearPagination()
    setIsUpdating(true)
    withPromise(saveManagementUIFilters, saveFiltersPayload).then(() => {
      setIsUpdating(false)
    })
  }

  const onRemoveAssigneesFilter = () => {
    const updatedOtherFiltersArr = getUpdatedOtherFiltersArr(
      FILTER_TYPE_ASSIGNEE,
    )

    setTmpUsers([])
    setTmpUserGroups([])
    setAssigneesSearchQuery('')
    setSelectedOtherFilters(updatedOtherFiltersArr)
    applyAssignees([], [])
  }

  const onRemoveCreatedByFilter = () => {
    const updatedOtherFiltersArr = getUpdatedOtherFiltersArr(
      FILTER_TYPE_CREATED_BY,
    )

    setTmpCreatedBy([])
    setCreatedBySearchQuery('')
    setSelectedOtherFilters(updatedOtherFiltersArr)
    applyFilter({
      [FILTER_TYPE_CREATED_BY]: [],
    })
  }

  const onRemoveRecurrenceFilter = () => {
    const updatedOtherFiltersArr = getUpdatedOtherFiltersArr(
      FILTER_TYPE_RECURRENCE,
    )

    setTmpRecurrence([])
    setSelectedOtherFilters(updatedOtherFiltersArr)
    applyFilter({
      [FILTER_TYPE_RECURRENCE]: [],
    })
  }

  const onRemoveStatusFilter = () => {
    const updatedOtherFiltersArr = getUpdatedOtherFiltersArr(FILTER_TYPE_STATUS)

    setTmpStatus([])
    setSelectedOtherFilters(updatedOtherFiltersArr)
    applyFilter({
      [FILTER_TYPE_STATUS]: [],
    })
  }

  const onRemoveMediaFilter = () => {
    const updatedOtherFiltersArr = getUpdatedOtherFiltersArr(FILTER_TYPE_MEDIA)

    setSelectedOtherFilters(updatedOtherFiltersArr)
    applyFilter({
      [FILTER_TYPE_MEDIA]: null,
    })
  }

  const onRemoveDueDateFilter = () => {
    const updatedOtherFiltersArr = getUpdatedOtherFiltersArr(
      FILTER_TYPE_DUE_DATE,
    )

    setSelectedOtherFilters(updatedOtherFiltersArr)
    applyFilter({
      [FILTER_TYPE_DUE_DATE]: null,
    })
  }

  const onCloseAssigneeMenu = () => {
    setAssigneeMenuAnchor(null)
    setAssigneesSearchQuery('')

    if (
      !isEqual(tmpUsers, prevUsers) ||
      !isEqual(tmpUserGroups, prevUserGroups)
    ) {
      const dataUsers = tmpUsers.map(({ id }) => id)
      const dataUserGroups = tmpUserGroups.map(({ id }) => id)

      applyAssignees(dataUsers, dataUserGroups)
    }
  }

  const onCloseCreatedByMenu = () => {
    setCreatedByMenuAnchor(null)
    setCreatedBySearchQuery('')

    if (!isEqual(tmpCreatedBy, prevCreatedBy)) {
      const data = tmpCreatedBy.map(({ id }) => id)

      applyOtherCreatedBy(data)
    }
  }

  const onCloseRecurrenceMenu = () => {
    if (!isEqual(tmpRecurrence, appliedRecurrence)) {
      applyFilter({
        [FILTER_TYPE_RECURRENCE]: tmpRecurrence,
      })
    }
  }

  const onCloseStatusMenu = () => {
    if (!isEqual(tmpStatus, appliedStatus)) {
      applyFilter({
        [FILTER_TYPE_STATUS]: tmpStatus,
      })
    }
  }

  const onCloseMediaMenu = (value: boolean) => {
    if (appliedMedia !== value) {
      applyFilter({
        [FILTER_TYPE_MEDIA]: value,
      })
    }
  }

  const onApplyDueDate = (value: Date | null) => {
    const formattedDate = dateToLocalISO8601DateString(value)

    if (formattedDate) {
      applyFilter({
        [FILTER_TYPE_DUE_DATE]: formattedDate,
      })
    }
  }

  const renderFilteredAssigneesList = () => (
    <div>
      {!!filteredUsersListFilter.length && (
        <>
          <MenuTitle>{formatMessage(messages.users)}</MenuTitle>
          <UsersSelectList
            list={filteredUsersListFilter}
            tmpUsersArr={tmpUsers}
            setTmpUsersArr={setTmpUsers}
          />
        </>
      )}
      {!!filteredUserGroupsListFilter.length && (
        <>
          <MenuTitle>{formatMessage(messages.teams)}</MenuTitle>
          <UsersSelectList
            list={filteredUserGroupsListFilter}
            tmpUsersArr={tmpUserGroups}
            setTmpUsersArr={setTmpUserGroups}
          />
        </>
      )}
      {!filteredUsersListFilter.length &&
        !filteredUserGroupsListFilter.length && (
          <ListItem>{formatMessage(messages.noResultsMessage)}</ListItem>
        )}
    </div>
  )

  const renderFilteredCreatedByList = () => (
    <div>
      {!!filteredCreatedByListFilter.length && (
        <>
          <MenuTitle>{formatMessage(messages.createdBy)}</MenuTitle>
          <UsersSelectList
            list={filteredCreatedByListFilter}
            tmpUsersArr={tmpCreatedBy}
            setTmpUsersArr={setTmpCreatedBy}
          />
        </>
      )}
      {!filteredCreatedByListFilter.length && (
        <ListItem>{formatMessage(messages.noResultsMessage)}</ListItem>
      )}
    </div>
  )

  const dateStringToLocalDate = (date: string | null) => {
    if (!date) return null

    return new DateFnsUtils().parse(date, 'yyyy-MM-dd')
  }

  const renderSelectedOtherFilterInput = ({
    value,
    label,
  }: SelectedOtherFilter) => {
    switch (value) {
      case FILTER_TYPE_ASSIGNEE:
        return (
          <OtherFilterListItem key={value}>
            <div>{formatMessage(messages[label as keyof typeof messages])}</div>
            <Flex>
              <UsersFilterButton isMenuOpen={!!assigneeMenuAnchor}>
                <Button
                  onClick={({ currentTarget }) => {
                    setAssigneeMenuAnchor(currentTarget)
                    setPrevUsers(tmpUsers)
                    setPrevUserGroups(tmpUserGroups)
                    setTimeout(() => {
                      if (assigneesSearchFieldRef.current) {
                        assigneesSearchFieldRef.current.focus()
                      }
                    }, 0)
                  }}
                  endIcon={<Vector />}>
                  <UsersListComponent
                    users={selectedUsers}
                    groups={selectedUserGroups}
                  />
                </Button>
              </UsersFilterButton>
              <UsersListDropdown
                anchor={assigneeMenuAnchor}
                onCloseMenu={onCloseAssigneeMenu}
                searchQuery={assigneesSearchQuery}
                setSearchQuery={setAssigneesSearchQuery}
                inputRef={assigneesSearchFieldRef}
                renderUserList={renderFilteredAssigneesList}
              />
              <RemoveFilterButton removeFn={onRemoveAssigneesFilter} />
            </Flex>
          </OtherFilterListItem>
        )
      case FILTER_TYPE_CREATED_BY:
        return (
          <OtherFilterListItem key={value}>
            <div>{formatMessage(messages[label as keyof typeof messages])}</div>
            <Flex>
              <UsersFilterButton isMenuOpen={!!createdByMenuAnchor}>
                <Button
                  onClick={({ currentTarget }) => {
                    setCreatedByMenuAnchor(currentTarget)
                    setPrevCreatedBy(tmpCreatedBy)
                    setTimeout(() => {
                      if (createdBySearchFieldRef.current) {
                        createdBySearchFieldRef.current.focus()
                      }
                    }, 0)
                  }}
                  endIcon={<Vector />}>
                  <UsersListComponent users={selectedCreatedBy} />
                </Button>
              </UsersFilterButton>
              <UsersListDropdown
                anchor={createdByMenuAnchor}
                onCloseMenu={onCloseCreatedByMenu}
                searchQuery={createdBySearchQuery}
                setSearchQuery={setCreatedBySearchQuery}
                inputRef={createdBySearchFieldRef}
                renderUserList={renderFilteredCreatedByList}
              />
              <RemoveFilterButton removeFn={onRemoveCreatedByFilter} />
            </Flex>
          </OtherFilterListItem>
        )
      case FILTER_TYPE_RECURRENCE:
        return (
          <OtherFilterListItem key={value}>
            <div>{formatMessage(messages[label as keyof typeof messages])}</div>
            <Flex>
              <FilterSelectContainer>
                <SelectInput
                  multiple
                  label=""
                  options={MANAGEMENT_UI_RECURRENCE_FILTER}
                  value={tmpRecurrence}
                  onChange={setTmpRecurrence}
                  onClose={onCloseRecurrenceMenu}
                  optionsMessages={messages}
                  customWidth="100%"
                />
              </FilterSelectContainer>
              <RemoveFilterButton removeFn={onRemoveRecurrenceFilter} />
            </Flex>
          </OtherFilterListItem>
        )
      case FILTER_TYPE_STATUS:
        return (
          <OtherFilterListItem key={value}>
            <div>{formatMessage(messages[label as keyof typeof messages])}</div>
            <Flex>
              <FilterSelectContainer>
                <SelectInput
                  multiple
                  label=""
                  options={MANAGEMENT_UI_STATUS_FILTER}
                  value={tmpStatus}
                  onChange={setTmpStatus}
                  onClose={onCloseStatusMenu}
                  optionsMessages={messages}
                  customWidth="100%"
                />
              </FilterSelectContainer>
              <RemoveFilterButton removeFn={onRemoveStatusFilter} />
            </Flex>
          </OtherFilterListItem>
        )
      case FILTER_TYPE_MEDIA:
        return (
          <OtherFilterListItem key={value}>
            <div>{formatMessage(messages[label as keyof typeof messages])}</div>
            <Flex>
              <FilterSelectContainer>
                <SelectInput
                  label=""
                  options={MANAGEMENT_UI_MEDIA_FILTER}
                  value={appliedMedia}
                  onChange={onCloseMediaMenu}
                  optionsMessages={messages}
                  customWidth="100%"
                />
              </FilterSelectContainer>
              <RemoveFilterButton removeFn={onRemoveMediaFilter} />
            </Flex>
          </OtherFilterListItem>
        )
      case FILTER_TYPE_DUE_DATE:
        return (
          <OtherFilterListItem key={value}>
            <div>{formatMessage(messages[label as keyof typeof messages])}</div>
            <DatePickerContainer align="center">
              <DatesIcon />
              <MuiPickersUtilsProvider utils={DateFnsUtils} locale={locale}>
                <DatePicker
                  autoOk
                  disableToolbar
                  allowKeyboardControl={false}
                  format={dateFormat}
                  value={dateStringToLocalDate(appliedDueDate) || null}
                  onChange={onApplyDueDate}
                />
              </MuiPickersUtilsProvider>
              <RemoveFilterButton removeFn={onRemoveDueDateFilter} />
            </DatePickerContainer>
          </OtherFilterListItem>
        )
      default:
        return (
          <ListItem key={value}>
            {formatMessage(messages[label as keyof typeof messages])}
          </ListItem>
        )
    }
  }

  return (
    <Menu
      anchorEl={otherFiltersMenuAnchor}
      setAnchorEl={setOtherFiltersMenuAnchor}
      title={formatMessage(messages.activeFilters)}
      renderMenuBody={() => (
        <div>
          {selectedFiltersForTab.map((filterItem: SelectedOtherFilter) =>
            renderSelectedOtherFilterInput(filterItem),
          )}
          {!allOtherFiltersSelected && (
            <ListItem>
              <AddFilterButton
                onClick={({ currentTarget }) => {
                  const paper = currentTarget.closest('.MuiMenu-paper')

                  if (paper) {
                    setOtherFiltersSubmenuAnchor(
                      paper.getElementsByClassName(
                        'MuiListItem-gutters',
                      )[0] as HTMLElement,
                    )
                  }
                }}>
                + {formatMessage(messages.addFilter)}
              </AddFilterButton>
              <OtherFilters
                activeTab={activeTab}
                anchorEl={otherFiltersSubmenuAnchor}
                setAnchorEl={setOtherFiltersSubmenuAnchor}
                applyFilter={applyFilter}
                closeOnSelect
              />
            </ListItem>
          )}
        </div>
      )}
    />
  )
}

export default connect(
  (state: {
    getIn: (
      path: Array<string>,
    ) =>
      | List<SelectedOtherFilter>
      | Map<string, User | UserGroup | Site>
      | Licensee
      | JobsManagementFilters
  }) => ({
    filtersList: state.getIn(['managementUI', 'filtersList']) as Map<
      string,
      User | UserGroup | Site
    >,
    selectedOtherFilters: state.getIn([
      'managementUI',
      'selectedOtherFilters',
    ]) as List<SelectedOtherFilter>,
    jobsManagementFilters: state.getIn([
      'profile',
      'profile',
      'jobsManagementFilters',
    ]) as JobsManagementFilters,
    licensee: state.getIn(['profile', 'profile', 'licensee']) as Licensee,
  }),
  {
    setSelectedOtherFilters,
    saveManagementUIFilters,
  },
)(injectIntl(SelectedOtherFiltersDropdown))
